import { PropsWithChildren, useMemo } from "react";

import { SearchContextProvider } from "@/features/search/context/SearchContext";
import { useCanonicalUrl } from "@/features/search/hooks/useCanonicalUrl";
import { useSearchResults } from "@/features/search/hooks/useSearch";
import { useSearchAnalytics } from "@/features/search/hooks/useSearchAnalytics";
import { useSearchNavigation } from "@/features/search/hooks/useSearchNavigation";
import { useSearchRoute } from "@/features/search/hooks/useSearchRoute";

/**
 * Wires URL -> route -> query -> context. Drop it above any search UI; all children
 * read state and drive navigation through `useSearchContext`, so results, facets and
 * pagination stay decoupled from one another — and from whether the URL is a query
 * search or a curated landing page.
 */
type SearchProviderProps = PropsWithChildren<{
  /** The server rendered without the viewer (WHO-5684); see `useSearchResults`. */
  hydratedAnonymously?: boolean;
}>;

export const SearchProvider = ({
  children,
  hydratedAnonymously,
}: SearchProviderProps) => {
  const route = useSearchRoute();
  const query = useSearchResults(route, { hydratedAnonymously });
  const navigation = useSearchNavigation();

  /* Here rather than in the page, so both surfaces that render results — the query search and
   * every landing page — report identically without either page knowing about analytics. */
  useSearchAnalytics(route, query);

  /* A search the service could express in params becomes those params — see the hook. */
  useCanonicalUrl(query, navigation.adopt);

  const value = useMemo(
    () => ({ route, query, navigation }),
    [route, query, navigation]
  );

  return (
    <SearchContextProvider value={value}>{children}</SearchContextProvider>
  );
};
